import React from "react";
import {
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";
import { Skeleton } from "primereact/skeleton";

import { KpiCard } from "../dashboard/KpiCard";

export type OperationalKpiIcon =
  | "inProgress"
  | "completed"
  | "manufacturedWeight"
  | "targetWeight";

const PLACEHOLDER_STATS: OperationalStat[] = [
  { name: "—", icon: "inProgress" },
  { name: "—", icon: "completed" },
  { name: "—", icon: "manufacturedWeight" },
  { name: "—", icon: "targetWeight" },
];

const KPI_ICONS: Record<
  OperationalKpiIcon,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  inProgress: ClockIcon,
  completed: CheckCircleIcon,
  manufacturedWeight: ScaleIcon,
  targetWeight: ChartBarIcon,
};

interface OperationalStat {
  name: string;
  stat?: string;
  subtitle?: string | null;
  icon?: OperationalKpiIcon;
}

interface OperationalAnalyticsStripProps {
  stats?: OperationalStat[];
  loading?: boolean;
}

function KpiValueSkeleton() {
  return (
    <Skeleton
      width="4rem"
      height="1.75rem"
      borderRadius="8px"
      className="inline-block max-w-full"
    />
  );
}

export default function OperationalAnalyticsStrip({
  stats = [],
  loading = false,
}: OperationalAnalyticsStripProps) {
  if (!loading && (!stats || stats.length === 0)) {
    return null;
  }

  if (loading && (!stats || stats.length === 0)) {
    return (
      <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {PLACEHOLDER_STATS.map((_, index) => (
          <Skeleton key={index} height="8.5rem" borderRadius="16px" />
        ))}
      </section>
    );
  }

  return (
    <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
      {stats.map((item, index) => {
        const iconKey =
          item.icon ??
          PLACEHOLDER_STATS[index % PLACEHOLDER_STATS.length].icon ??
          "inProgress";

        return (
          <KpiCard
            key={`${item.name}-${index}`}
            label={item.name}
            value={loading ? <KpiValueSkeleton /> : (item.stat ?? "—")}
            hint={item.subtitle}
            icon={KPI_ICONS[iconKey]}
          />
        );
      })}
    </section>
  );
}
